"use client";

import { useEffect, useState } from "react";

const STORAGE_KEY = "piOfferDeadline";
const WINDOW_MS = 20 * 60 * 1000;

function pad(value: number) {
  return String(value).padStart(2, "0");
}

/**
 * Evergreen urgency countdown for the offer banners: rolls forward to a fresh 20-minute deadline
 * whenever it hits zero, so the "offer ends in" timer never shows as expired. The deadline is
 * persisted in localStorage so a reload mid-session keeps counting down rather than resetting.
 */
export function useOfferCountdown() {
  const [secondsLeft, setSecondsLeft] = useState<number>();

  useEffect(() => {
    const readOrCreateDeadline = () => {
      const stored = Number(localStorage.getItem(STORAGE_KEY));
      if (stored && stored - Date.now() > 0) return stored;

      const deadline = Date.now() + WINDOW_MS;
      localStorage.setItem(STORAGE_KEY, String(deadline));
      return deadline;
    };

    let deadline = readOrCreateDeadline();

    const tick = () => {
      let left = Math.round((deadline - Date.now()) / 1000);
      if (left <= 0) {
        deadline = Date.now() + WINDOW_MS;
        localStorage.setItem(STORAGE_KEY, String(deadline));
        left = Math.round(WINDOW_MS / 1000);
      }
      setSecondsLeft(left);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  if (secondsLeft === undefined) {
    return { hours: "00", minutes: "00", seconds: "00", clock: undefined };
  }

  const hours = pad(Math.floor(secondsLeft / 3600));
  const minutes = pad(Math.floor((secondsLeft % 3600) / 60));
  const seconds = pad(secondsLeft % 60);

  return { hours, minutes, seconds, clock: `${hours}:${minutes}:${seconds}` };
}
